import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BREAKPOINTS } from '../../shared/constants';
import { WindowService } from '../../shared/services/window.service';
import { ListViewHelper } from './list-view.helper';
import { ListViewConfiguration } from './list-view.model';
import { checkInputsForText } from '../../design-system.helper';
import { DesignSystemService } from '../../design-system.service';

@Component({
  selector: 'list-view',
  styleUrls: ['./list-view.component.scss'],
  templateUrl: './list-view.component.html',
})
export class ListViewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() config: ListViewConfiguration;
  @Input() data: object[];
  @Input() hasAnErrorOccurred = false;
  @Input() currentSortOrder: string;
  @Input() exportError: string;
  @Input() disableExportButton: boolean;
  @Input() showErrorModal: boolean;
  @Output() actionButtonClicked: EventEmitter<any> = new EventEmitter();
  @Output() exportClicked: EventEmitter<any> = new EventEmitter();
  @Output() filtered: EventEmitter<object> = new EventEmitter();
  @Output() pageClicked: EventEmitter<number> = new EventEmitter();
  @Output() rowClicked: EventEmitter<object> = new EventEmitter();
  @Output() sorted: EventEmitter<string> = new EventEmitter();
  @Output() sortHeader: EventEmitter<string> = new EventEmitter();
  @Output() tabClicked: EventEmitter<number> = new EventEmitter();
  @Output() typeAheadOptionSelected: EventEmitter<object> = new EventEmitter();
  @Output() actionSelected: EventEmitter<object> = new EventEmitter();
  @Output() errorModalClosed: EventEmitter<boolean> = new EventEmitter();
  @ViewChild('theDatagrid') theDatagrid;

  activeTab;
  areMobileFiltersOpen: boolean;
  filtersForm: UntypedFormGroup = new UntypedFormGroup({});
  filtersFormValueString: string;
  isMobileView: boolean;
  private eventListener: () => void;

  get title(): string {
    return this.config.title;
  }

  get hasTabs(): boolean {
    return !!this.config.tabs;
  }

  get paginator() {
    return this.config.paginator;
  }

  get description(): string {
    return this.hasTabs ? this.activeTab.description : this.config.description;
  }

  get filters() {
    return this.hasTabs ? this.activeTab.filters : this.config.filters;
  }

  get notes() {
    return this.hasTabs ? this.activeTab.notes : this.config.notes;
  }

  get export() {
    return this.hasTabs ? this.activeTab.export : this.config.export;
  }

  get exportDropdown() {
    return this.config.exportDropdown ? this.config.exportDropdown : null;
  }

  get datagrid() {
    return this.hasTabs
      ? this.activeTab.datagrid
      : this.config.datagrid || null;
  }

  handleDataGridSort(prop: string) {
    this.sorted.emit(prop);
  }

  handleDataGridSortHeader(prop: string) {
    this.sortHeader.emit(prop);
  }

  constructor(
    private listViewHelper: ListViewHelper,
    private window: WindowService,
    private el: ElementRef,
    private designSystemService: DesignSystemService,
  ) {}

  @HostListener('window:resize')
  onResize() {
    this.filtersForm.patchValue(this.filtersForm.value);
    this.isMobileView =
      this.window.nativeWindow.innerWidth < BREAKPOINTS.MEDIUM;
  }

  ngOnInit(): void {
    const [config, data] = checkInputsForText([this.config, this.data]);

    this.config = config as ListViewConfiguration;
    this.data = data as object[];

    this.activeTab = this.hasTabs
      ? this.config.tabs.activeTab
        ? this.config.tabs.activeTab
        : this.config.tabs.allTabs[0]
      : undefined;
    this.onResize();

    this.eventListener = this.designSystemService.listenForAttributeUpdates(
      this,
      this.el.nativeElement,
    );
  }

  ngOnDestroy() {
    if (this.eventListener) {
      this.eventListener();
    }
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    const changedFilters = simpleChanges['config'];

    if (changedFilters && !changedFilters.firstChange) {
      const oldConfig = JSON.stringify(changedFilters?.previousValue);
      const newConfig = JSON.stringify(changedFilters?.currentValue);

      if (oldConfig !== newConfig) {
        // need to refresh the form
        const tabs = this.config.tabs;

        if (tabs?.allTabs?.length > 0) {
          this.activeTab = !this.activeTab
            ? changedFilters.currentValue.tabs.allTabs[0]
            : changedFilters.currentValue.tabs.allTabs.find(
                (tab) => this.activeTab.key === tab.key,
              );
        }
      }
    }

    const exportError = simpleChanges['exportError'];

    if (exportError && !exportError.firstChange && !!exportError.currentValue) {
      this.showErrorModal = true;
    }
  }

  cancelMobileFiltersOpen(keepOpen = false) {
    this.areMobileFiltersOpen = keepOpen;
  }

  openMobileFilters() {
    this.areMobileFiltersOpen = true;
  }

  applyFilters(keepOpen = false) {
    if (!keepOpen) {
      this.areMobileFiltersOpen = false;
    }
    this.filtered.emit(this.filtersForm.value);
    this.filtersFormValueString = this.listViewHelper.buildFiltersString(
      this.filters,
      this.filtersForm,
    );
  }

  checkForDefaults() {
    // For dropdowns use the only value in the list
    // with tabs, the filters config will be on the tab so check for it there
    const filtersConfig = this.activeTab
      ? this.activeTab.filters
      : this.config.filters;

    filtersConfig?.controls?.forEach((ctrl) => {
      if (
        !ctrl.endpoint &&
        !ctrl.placeholder &&
        ctrl['options'] &&
        ctrl['options'].length === 1
      ) {
        const controlName = ctrl.prop;

        this.filtersForm.controls[controlName].setValue(
          ctrl['options'][0].value,
        );
      }
    });
  }

  clearFilters() {
    this.filtersForm.reset();
    this.checkForDefaults();
    this.applyFilters(true);
  }

  handleFormCreated() {
    this.listViewHelper.buildFiltersString(this.filters, this.filtersForm);
  }

  setActiveTab(index: number) {
    this.tabClicked.emit(index);
    this.activeTab = this.config.tabs.allTabs[index];
    this.clearFilters();
  }

  setCurrentPage(page: number) {
    const sizeOfRem = 16;
    const numberOfRem = -7;

    this.theDatagrid.nativeElement.scrollIntoView(true);
    this.window.nativeWindow.scrollBy(0, sizeOfRem * numberOfRem);
    this.pageClicked.emit(page);
  }

  setActiveRow(row: object) {
    this.rowClicked.emit(row);
  }

  exportDatagrid(fileType?: string) {
    this.exportClicked.emit(fileType);
  }

  handleClickAction(event: any) {
    this.actionButtonClicked.emit(event);
  }

  handleTypeaheadOptionSelected(event: any) {
    this.typeAheadOptionSelected.emit(event);
  }

  handleActionLinkClicked(actionLink) {
    this.actionSelected.emit(actionLink);
  }

  closeErrorModal() {
    this.showErrorModal = false;
    this.errorModalClosed.emit(true);
  }
}
